import { Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';

// ── Apply button (shared by the Post Processing tabs) ───────
//
// Apply commits the result the clinician is looking at, so it is available
// only while a preview for the CURRENT settings is on screen. Every tab
// previews on its own as its settings change, so that window is open
// whenever there is a result to commit.
//
// Apply is additionally held while the operation cannot run at all: a
// commit already in flight, a preview compute holding the GPU, and the
// tab's own preconditions (no box, no criterion enabled, no grid to convert
// through, a tilted series) — passed in as `applyDisabled`.
//
// It is also held while the edit target is unresolved, so a preview that
// went ready before the target was lost does not leave a live Apply over a
// structure set that is not on screen. The commit itself is refused by
// useRoiSdfOperation; this keeps the button honest about it.

export default function OperationApplyButton({
    onApply,
    isPreviewing,
    isPreviewReady,
    isProcessing,
    applyDisabled = false,
    announcesPreviewing = true,
}: {
    onApply: () => void;
    isPreviewing: boolean;
    /**
     * A preview of the current settings is on screen. Apply commits what
     * that preview shows, so it is the readiness term.
     */
    isPreviewReady: boolean;
    isProcessing: boolean;
    /**
     * The tab's own precondition for the operation being runnable, distinct
     * from preview readiness.
     */
    applyDisabled?: boolean;
    /**
     * Whether this button reports the preview compute. True where it is the
     * ONLY button in the row (Crop), so "Previewing..." has nowhere else to
     * appear. False inside {@link PreviewApplyRow}, where the Preview button
     * beside it already says so — two adjacent buttons both reading
     * "Previewing..." says nothing about which one the user pressed.
     */
    announcesPreviewing?: boolean;
}) {
    const { targetUnresolved } = useStructureToolGating();
    return (
        <Button
            // Contained: it is the panel's one committing action, and must
            // not read at the same weight as the controls that set it up.
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={onApply}
            disabled={
                isProcessing ||
                isPreviewing ||
                !isPreviewReady ||
                applyDisabled ||
                targetUnresolved
            }
        >
            {isProcessing ? (
                <FormattedMessage id="postProcessing.applying" />
            ) : isPreviewing && announcesPreviewing ? (
                <FormattedMessage id="postProcessing.previewing" />
            ) : (
                <FormattedMessage id="postProcessing.apply" />
            )}
        </Button>
    );
}
